import { useTheme, type ResolvedTheme } from '../lib/theme';

/**
 * Theme toggle button. One tap cycles light → dark → system. The
 * icon reflects the currently resolved theme (sun in light, moon in
 * dark) with a small system-following dot when on 'system' mode.
 *
 * Lives in the AppHeader for authed pages and the top-right of
 * TwoPaneLayout for public pages.
 */
export function ThemeToggle({ size = 36 }: { size?: number }) {
  const { choice, resolved, toggle } = useTheme();
  const showSystemBadge = choice === 'system';

  const tip =
    choice === 'light'
      ? 'Light theme — click for dark'
      : choice === 'dark'
        ? 'Dark theme — click to follow system'
        : `System theme (${resolved}) — click for light`;

  return (
    <button
      type="button"
      onClick={toggle}
      title={tip}
      aria-label={tip}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: '1px solid var(--aqua-border)',
        background: 'var(--aqua-surface)',
        color: 'var(--aqua-text)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        position: 'relative',
        transition:
          'background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor =
          'var(--aqua-primary)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.borderColor =
          'var(--aqua-border)';
      }}
    >
      {resolved === 'dark' ? <MoonIcon /> : <SunIcon />}
      {showSystemBadge && <SystemDot resolved={resolved} />}
    </button>
  );
}

function SunIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3a6.5 6.5 0 0 0 9 9 9 9 0 1 1-9-9z" />
    </svg>
  );
}

/** Small dot in the corner of the button when 'system' mode is active. */
function SystemDot({ resolved }: { resolved: ResolvedTheme }) {
  return (
    <span
      style={{
        position: 'absolute',
        right: 5,
        bottom: 5,
        width: 6,
        height: 6,
        borderRadius: '50%',
        background:
          resolved === 'dark' ? 'var(--aqua-primary)' : 'var(--aqua-navy)',
        border: '1px solid var(--aqua-surface)',
      }}
      aria-hidden="true"
    />
  );
}
